import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, Mic, X, TrendingUp, Clock } from "lucide-react";
import { api } from "@/lib/api";

const RECENT_KEY = "sw_recent_searches";

function getRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch (e) { return []; }
}
function saveRecent(q) {
  if (!q) return;
  try {
    const cur = getRecent().filter(x => x !== q);
    cur.unshift(q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, 6)));
  } catch (e) { /* ignore */ }
}

export default function SearchBar({ initialQuery = "", autoFocus = false, sticky = false }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [trending, setTrending] = useState([]);
  const [recent, setRecent] = useState(getRecent());
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Trending — fetch once
  useEffect(() => {
    api.get("/search/trending").then(r => setTrending(r.data?.trending || [])).catch(() => {});
  }, []);

  // Autocomplete with 200ms debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(() => {
      api.get(`/search/suggest?q=${encodeURIComponent(q)}`).then(r => setSuggestions(r.data?.suggestions || [])).catch(() => {});
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submit = (text) => {
    const v = (text ?? q).trim();
    if (!v) return;
    saveRecent(v);
    setRecent(getRecent());
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(v)}`);
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice search isn't supported in this browser"); return; }
    const rec = new SR();
    rec.lang = "en-IN";
    rec.continuous = false;
    rec.interimResults = false;
    setListening(true);
    rec.onresult = (e) => {
      const txt = e.results[0][0].transcript;
      setQ(txt);
      setListening(false);
      submit(txt);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    try { rec.start(); } catch (e) { setListening(false); }
  };

  const clearRecent = () => { localStorage.removeItem(RECENT_KEY); setRecent([]); };

  return (
    <div ref={wrapRef} className={`relative w-full ${sticky ? "sticky top-14 z-30" : ""}`} data-testid="searchbar">
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="relative">
        <div className="flex items-center bg-white border-2 border-amethyst-200 hover:border-magenta-300 focus-within:border-magenta-500 rounded-full shadow-sm transition px-4 py-2.5">
          <SearchIcon className="w-5 h-5 text-amethyst-500 flex-shrink-0" strokeWidth={2} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search products, sellers, categories…"
            autoFocus={autoFocus}
            className="flex-1 ml-3 bg-transparent outline-none text-sm sm:text-base text-amethyst-900 placeholder:text-gray-400"
            data-testid="search-input"
          />
          {q && (
            <button type="button" onClick={() => { setQ(""); setSuggestions([]); inputRef.current?.focus(); }} className="ml-2 text-gray-400 hover:text-red-500 p-1" data-testid="search-clear">
              <X className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={startVoice} aria-label="Voice search" className={`ml-1 p-2 rounded-full transition ${listening ? "bg-magenta-100 text-magenta-700 animate-pulse" : "text-amethyst-600 hover:bg-amethyst-50"}`} data-testid="search-voice">
            <Mic className="w-4 h-4" />
          </button>
        </div>
      </form>

      {open && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl border border-amethyst-100 shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto" data-testid="search-dropdown">
          {suggestions.length > 0 && (
            <div className="p-2">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-amethyst-700 px-2 py-1">Suggestions</p>
              {suggestions.map((s) => (
                <button key={s.id} type="button" onClick={() => submit(s.name)} className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-amethyst-50 text-left" data-testid={`suggestion-${s.id}`}>
                  {s.image_url ? <img src={s.image_url} alt="" className="w-9 h-9 rounded object-contain object-center bg-white p-0.5 border border-amethyst-100" /> : <div className="w-9 h-9 rounded bg-amethyst-100" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amethyst-900 line-clamp-1">{s.name}</p>
                    <p className="text-[11px] text-gray-500 line-clamp-1">{s.category} · ₹{s.price?.toLocaleString("en-IN")}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {q.length < 2 && recent.length > 0 && (
            <div className="p-2 border-t border-amethyst-50 first:border-t-0">
              <div className="flex items-center justify-between px-2 py-1">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-amethyst-700 flex items-center gap-1"><Clock className="w-3 h-3" /> Recent</p>
                <button type="button" onClick={clearRecent} className="text-[10px] text-gray-400 hover:text-red-500">Clear</button>
              </div>
              <div className="flex flex-wrap gap-1.5 p-1">
                {recent.map(r => (
                  <button key={r} type="button" onClick={() => { setQ(r); submit(r); }} className="text-xs px-3 py-1 rounded-full bg-amethyst-50 text-amethyst-800 hover:bg-amethyst-100">{r}</button>
                ))}
              </div>
            </div>
          )}

          {q.length < 2 && trending.length > 0 && (
            <div className="p-2 border-t border-amethyst-50">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-amethyst-700 px-2 py-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Trending</p>
              <div className="flex flex-wrap gap-1.5 p-1">
                {trending.map(t => (
                  <button key={t} type="button" onClick={() => { setQ(t); submit(t); }} className="text-xs px-3 py-1 rounded-full bg-magenta-50 text-magenta-700 hover:bg-magenta-100">{t}</button>
                ))}
              </div>
            </div>
          )}

          {q.length >= 2 && suggestions.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-500" data-testid="search-empty">No suggestions. Press Enter to search.</div>
          )}
        </div>
      )}
    </div>
  );
}
